import React from "react";
import { SpotShape } from "../../../../types";
import { useMapCanvas } from "../../contexts/MapCanvasContext";
import { DEFAULT_NODE_RECT_COLOR } from "../../defines";
import { useShape } from "../ShapeContext/useShape";

/**
 * Resolves the display color for a spot shape using the priority chain:
 * 1. Manual — user explicitly set via color picker (colorSource: 'manual' or legacy shape.color)
 * 2. Canvas group — parent GroupShape.color when spot is a group child
 * 3. Backend team — team/department color from resource's assigned group
 * 4. Default — DEFAULT_NODE_RECT_COLOR (#E8ECEF)
 */
export const useSpotColor = (shape: SpotShape): string => {
  const { parentGroupColor } = useShape();
  const { teamColors, resourceColors } = useMapCanvas();

  return React.useMemo(() => {
    // 0. Host override (heatmaps) — the data decides, whatever the map says
    if (shape.resource_id && resourceColors?.[shape.resource_id]) {
      return resourceColors[shape.resource_id];
    }

    // 1. Manual color — explicitly set by user, or legacy shape.color with no colorSource
    if (shape.colorSource === "manual" && shape.color) {
      return shape.color;
    }

    // Backward compat: existing spots with shape.color but no colorSource → treat as manual
    if (!shape.colorSource && shape.color) {
      return shape.color;
    }

    // 2. Canvas group color — inherited from parent group
    if (parentGroupColor) {
      return parentGroupColor;
    }

    // 3. Backend team color — from resource's team
    if (shape.resource_id && teamColors) {
      const teamColor = teamColors[shape.resource_id];
      if (teamColor) {
        return teamColor;
      }
    }

    // 4. Default
    return DEFAULT_NODE_RECT_COLOR;
  }, [
    shape.color,
    shape.colorSource,
    shape.resource_id,
    parentGroupColor,
    teamColors,
    resourceColors
  ]);
};
